import React, { useEffect, useState } from 'react';
import Loader from "../Loader/loader";
import LineSeparator from "../bgMadness/line";
import CardGrid from "../gamecard/cardGrid";
import AllGames from "../../data/gameData.json";
import AllCategories from "../../data/category.json";
import { Helmet } from 'react-helmet-async';
import BgMadness from "../bgMadness/bgMadness";
import { Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import generatePaginationNumbers from '../utitlity/generatePaginationNumbers.jsx'

const GAMES_PER_PAGE = 24;

const CategoryList = () => {
  const [allGames, setAllGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [category, setCategory] = useState("ALL");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  // Read from URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let catParam = queryParams.get("c") || "";
    const pageParam = parseInt(queryParams.get("page") || "1", 10);
    if(!catParam){
      catParam  = "ALL"
    }
    setCategory(catParam);
    setPage(pageParam);
  }, [location.search]);

  // Filter games
  useEffect(() => {
    let filtered = AllGames.data.filter(g => g.category === category);

    if (filtered.length === 0 || category === "") {
      const prefix = category?.substring(0, 3) || "data";
      const matchedKey = Object.keys(AllGames).find(key => key.startsWith(prefix));
      filtered = matchedKey && AllGames[matchedKey] ? AllGames[matchedKey] : AllGames.data;
    }

    setFilteredGames(filtered);
    setLoading(false);
  }, [category]);

  useEffect(() => {
    const startIndex = (page - 1) * GAMES_PER_PAGE;
    const paginated = filteredGames.slice(startIndex, startIndex + GAMES_PER_PAGE);
    setAllGames(paginated);
  }, [filteredGames, page]);

  const totalPages = Math.ceil(filteredGames.length / GAMES_PER_PAGE);

  const handleCategoryClick = (cat) => {
    navigate(`?c=${encodeURIComponent(cat)}&page=1`);
  };

  const handlePageChange = (newPage) => {
    navigate(`?c=${encodeURIComponent(category)}&page=${newPage}`);
  };

  // Find current category details for content and FAQ
  let categoryDetails = AllCategories.find(cat => cat.title === category);


  return (
    <div className="relative min-h-screen bg-[#0d0d1a] text-[#00F0FF] px-4 sm:px-6 lg:px-10 py-8 overflow-hidden">
      <Helmet>
        <title>{category ? `${category} Games | BrowserArena` : 'Categories | BrowserArena'}</title>
        <meta name="description" content="Search and filter hundreds of free browser games by category, tags, or titles." />
        <link rel="canonical" href="https://browserarena.com/categories" />
      </Helmet>

      {/* Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <BgMadness />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Category Filter */}
        <section className="mt-5 px-6 py-10 max-w-5xl mx-auto rounded-2xl border border-[#00F0FF] bg-[#111122]/80 shadow-lg shadow-[#00F0FF]/20 backdrop-blur-lg text-[#CCF6FF]">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-1">
            {AllCategories.map((cat, index) => (
              <Button
                key={index}
                onClick={() => handleCategoryClick(cat.title)}
                className={`text-sm text-center px-3 py-2 rounded border w-full transition font-semibold
                  ${category === cat.title
                    ? "bg-[#FF007F] text-white border-[#FF007F]"
                    : "bg-[#1a1a2e] text-[#00F0FF] border-[#00F0FF] hover:bg-[#222244] hover:border-[#FF007F]"}
                `}
              >
                {cat.title}
              </Button>
            ))}
          </div>
        </section>

        <LineSeparator />

        {/* Grid */}
        {loading ? <Loader /> : <CardGrid allGames={allGames} />}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-10 flex justify-center items-center gap-2 flex-wrap">
            {/* Prev */}
            <Button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="bg-[#00f0ff] text-black hover:bg-[#00e0e0] px-4 py-2 rounded font-bold disabled:opacity-40"
            >
              ⬅ Prev
            </Button>

            {generatePaginationNumbers(page, totalPages).map((item, index) => (
              item === "..." ? (
                <span key={index} className="text-[#888] px-2">...</span>
              ) : (
                <Button
                  key={index}
                  onClick={() => handlePageChange(item)}
                  className={`px-3 py-1 border ${item === page
                      ? "bg-[#FF007F] text-white border-[#FF007F]"
                      : "bg-[#1a1a2e] text-[#00F0FF] border-[#00F0FF] hover:border-[#FF007F]"
                    }`}
                >
                  {item}
                </Button>
              )
            ))}

            {/* Next */}
            <Button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="bg-[#00f0ff] text-black hover:bg-[#00e0e0] px-4 py-2 rounded font-bold disabled:opacity-40"
            >
              Next ➡
            </Button>
          </div>
        )}

        {/* Content & FAQ Section */}
        {categoryDetails && (
          <section className="mt-16 max-w-5xl mx-auto text-[#CCF6FF] space-y-10">
            {/* Content */}
            {categoryDetails.content && (
              <article className="prose prose-invert max-w-none text-[#00F0FF]">
                <h2 className="text-3xl font-bold mb-4">{categoryDetails.title} - More About This Category</h2>
                <div dangerouslySetInnerHTML={{ __html: categoryDetails.content }} />
              </article>
            )}

            {/* FAQ */}
            {categoryDetails.faq && categoryDetails.faq.length > 0 && (
              <section className="mt-10">
                <h3 className="text-2xl font-bold mb-6">Frequently Asked Questions</h3>
                <div className="space-y-6">
                  {categoryDetails.faq.map((faq, idx) => (
                    <div key={idx} className="bg-[#111122]/80 p-5 rounded-lg border border-[#00F0FF]">
                      <h4 className="font-semibold text-[#FF007F] mb-2">{faq.question}</h4>
                      <p className="text-[#CCF6FF]">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </section>
        )}

      </div>
    </div>
  );
};

export default CategoryList;
